<script setup>
import { Head } from '@inertiajs/vue3';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
</script>

<template>
    <Head title="Privacyverklaring – besapro" />

    <div class="font-sans text-gray-900 antialiased">
        <div class="pt-4 bg-gray-100">
            <div class="min-h-screen flex flex-col items-center pt-6 sm:pt-0">
                <div>
                    <AuthenticationCardLogo />
                </div>

                <div
                    class="w-full sm:max-w-2xl mt-6 p-6 bg-white shadow-md overflow-hidden sm:rounded-lg prose"
                >
                    <h1>Privacyverklaring – besapro</h1>
                    <p>Laatst bijgewerkt: 6&nbsp;augustus&nbsp;2025</p>

                    <!-- ──────────────────────────────────── -->
                    <h2>1. Identiteit &amp; contact</h2>
                    <p>
                        <strong>Pro-fase&nbsp;BV</strong><br>
                        Handelsnaam: <strong>besapro</strong><br>
                        Gasthuisstraat&nbsp;11, 1785 Merchtem, België<br>
                        BTW&nbsp;BE1000.844.911S<br>
                        E-mail: <a href="mailto:privacy@besapro.eu">privacy@besapro.eu</a> – Tel.: +32 486 92 63 21
                    </p>
                    <p>
                        Pro-fase BV is verwerkings­verantwoordelijke voor de gegevens die in het kader van de module
                        <em>Timesheets</em> worden opgeslagen en verwerker voor data die via de module <em>Sync</em>
                        enkel tijdelijk wordt doorgestuurd.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>2. Reikwijdte</h2>
                    <p>
                        Deze verklaring geldt voor alle (web-)applicaties, API’s en ondersteunende diensten die onder de
                        merknaam besapro worden aangeboden. De Dienst is uitsluitend bestemd voor rechtspersonen
                        (B2B); wij verwerken niet bewust gegevens van consumenten.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>3. Welke persoonsgegevens verwerken wij?</h2>

                    <table>
                        <thead><tr><th>Categorie</th><th>Voorbeelden</th><th>Retentie­termijn</th></tr></thead>
                        <tbody>
                            <tr>
                                <td>Account- &amp; contactgegevens</td>
                                <td>Naam contactpersoon, zakelijk e-mailadres, telefoon</td>
                                <td>Tot 12 mnd. na beëindiging account</td>
                            </tr>
                            <tr>
                                <td>Authenticatiegegevens</td>
                                <td>Hash van wachtwoord, 2FA-secret</td>
                                <td>Actieve duur account</td>
                            </tr>
                            <tr>
                                <td>Facturatiegegevens</td>
                                <td>Ondernemingsnummer, btw-nummer, factuuradres</td>
                                <td>7 jr. (fiscale bewaarplicht)</td>
                            </tr>
                            <tr>
                                <td>Log- &amp; gebruiksgegevens</td>
                                <td>IP-adres, browseragent, tijdstempels, API-calls</td>
                                <td>90 dagen, tenzij langer vereist voor security</td>
                            </tr>
                            <tr>
                                <td>Synchronisatiegegevens (<em>Sync</em>)</td>
                                <td>Gegevens uit Teamleader e.d.</td>
                                <td>Max. 168 u (1 week). (technische buffer)</td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- ──────────────────────────────────── -->
                    <h2>4. Doeleinden &amp; rechtsgrond</h2>
                    <ul>
                        <li><strong>Uitvoering overeenkomst</strong> (art.&nbsp;6 lid 1 b AVG): aanmaken en beheren van accounts, leveren van functionaliteit, klanten­support.</li>
                        <li><strong>Wettelijke verplichting</strong> (art.&nbsp;6 lid 1 c): fiscale administratie en boekhouding.</li>
                        <li><strong>Gerechtvaardigd belang</strong> (art.&nbsp;6 lid 1 f): beveiliging, log-analyse, verbeteren en beveiligen van onze Diensten, direct zakelijke communicatie met bestaande klanten.</li>
                        <li><strong>Toestemming</strong> (art.&nbsp;6 lid 1 a): opt-in marketingnieuwsbrief. U kunt toestemming te allen tijde intrekken.</li>
                    </ul>

                    <!-- ──────────────────────────────────── -->
                    <h2>5. Ontvangers &amp; (sub)verwerkers</h2>
                    <p>Wij delen gegevens uitsluitend met:</p>
                    <ul>
                        <li>Hosting-provider binnen de EU (ISO&nbsp;27001-gecertificeerd);</li>
                        <li>Betaaldienstverlener voor verwerking van SEPA/creditcard-betalingen;</li>
                        <li>Teamleader en andere platform-API’s die u zelf koppelt (bij gebruik van <em>Sync</em>);</li>
                        <li>Externe adviseurs (bv. accountants) wanneer dit wettelijk verplicht is.</li>
                    </ul>
                    <p>
                        Wanneer ontvangers buiten de Europese Economische Ruimte gevestigd zijn,
                        maken wij gebruik van een adequaatheids­besluit of door de Europese Commissie
                        goedgekeurde Standard Contractual Clauses.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>6. Cookies &amp; tracking</h2>
                    <p>
                        Wij gebruiken uitsluitend functionele cookies en beperkte analytische cookies (geanonimiseerd IP) om
                        de werking van het platform te verbeteren. Voor marketing-cookies vragen wij voorafgaande
                        toestemming.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>7. Beveiliging</h2>
                    <p>
                        Gegevens worden beveiligd via TLS-encryptie, hashing van wachtwoorden (Argon2), netwerk-firewalls,
                        rol-gebaseerde toegangs­controle en versleutelde back-ups. Toegang tot productie­omgevingen
                        is beperkt tot geautoriseerd personeel op basis van least-privilege.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>8. Uw privacyrechten</h2>
                    <p>Onder de AVG heeft u recht op:</p>
                    <ol>
                        <li>Inzage in uw persoonsgegevens;</li>
                        <li>Rectificatie;</li>
                        <li>Verwijdering (“recht op vergetelheid”);</li>
                        <li>Beperking van verwerking;</li>
                        <li>Overdraagbaarheid van gegevens;</li>
                        <li>Bezwaar tegen verwerking op basis van gerechtvaardigd belang;</li>
                        <li>Intrekking van gegeven toestemming zonder terug­werkende kracht.</li>
                    </ol>
                    <p>
                        Verzoeken kunt u kosteloos richten aan
                        <a href="mailto:info@besapro.eu">info@besapro.eu</a>.
                        Wij reageren binnen uiterlijk één maand.
                        U heeft tevens het recht een klacht in te dienen bij de
                        Gegevens­beschermings­autoriteit (<a href="https://www.gegevensbeschermingsautoriteit.be/" target="_blank" rel="noopener">GBA</a>).
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>9. Geautomatiseerde besluitvorming</h2>
                    <p>
                        Wij maken geen gebruik van uitsluitend geautomatiseerde besluitvorming die
                        rechtsgevolgen voor u heeft of u anderszins in aanmerkelijke mate treft.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>10. Bewaartermijnen</h2>
                    <p>
                        Wij bewaren persoonsgegevens niet langer dan noodzakelijk voor de hierboven beschreven
                        doeleinden of om te voldoen aan wettelijke bewaarplichten. Na afloop worden gegevens
                        verwijderd of geanonimiseerd.
                    </p>

                    <!-- ──────────────────────────────────── -->
                    <h2>11. Wijzigingen in deze verklaring</h2>
                    <p>
                        Wij kunnen deze privacyverklaring aanpassen. Bij materiële wijzigingen ontvangt u minimaal
                        dertig (30) dagen van tevoren een bericht via e-mail en/of het platform.
                    </p>

                    <h2>12. Contact</h2>
                    <p>
                        Vragen over privacy? Neem contact op via
                        <a href="mailto:info@besapro.eu">info@besapro.eu</a>.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
